import React from "react";
import { Popover } from "@mui/material";

import {
  CitationPassagesContext,
  needsPassage,
  openChatCitationWithPassage,
} from "@/components/common/citationPassages";

import {
  ChatCalculationSource,
  EvidenceSource,
  chatSourceLabel,
} from "../evidenceFooter";
import CalculationCard from "./CalculationCard";
import { sourceKindLabel, sourceTarget } from "./sourceTargets";

// The list of cited sources behind a "N sources" doorway (#11426 rule 7):
// each row led by the citation number its chips carry, each opening what
// its chip opens. Past six rows the list scrolls and states its denominator.
// Shared by the chat evidence footer and every artifact pane header
// (#11975), so a table, a document and a reply list their sources the same
// way.

/** Rows visible before the list scrolls (rule 4: a denominator, never a cut). */
export const SOURCES_VISIBLE_ROWS = 6;
const SOURCE_ROW_HEIGHT_PX = 52;

const pagesClause = (pages: number[]): string | null =>
  pages.length > 0 ? `p. ${pages.join(", ")}` : null;

interface SourcesPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sources: EvidenceSource[];
  citationQueryId?: string;
  // The host page draws its own split-pane viewer (the agents run view,
  // #11987): a listed source opens there, not in Layout's global one.
  inlineViewerOnly?: boolean;
}

const SourcesPopover = ({
  anchorEl,
  onClose,
  sources,
  citationQueryId,
  inlineViewerOnly,
}: SourcesPopoverProps) => {
  const scrolls = sources.length > SOURCES_VISIBLE_ROWS;
  const passages = React.useContext(CitationPassagesContext);
  // A calculation has nothing to open in a viewer: its row opens its card,
  // anchored to the row, over the list (#13618).
  const [card, setCard] = React.useState<{
    anchor: HTMLElement;
    calculation: ChatCalculationSource;
  } | null>(null);
  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "10px",
              border: "1px solid var(--fp-border)",
              boxShadow: "var(--fp-lift)",
            },
          },
        }}
      >
        <div
          role="dialog"
          aria-label="Sources"
          className="py-1 min-w-[260px] max-w-[380px]"
        >
          <ul
            className={scrolls ? "overflow-y-auto" : undefined}
            style={
              scrolls
                ? { maxHeight: SOURCES_VISIBLE_ROWS * SOURCE_ROW_HEIGHT_PX }
                : undefined
            }
          >
            {sources.map((source) => {
              const pages = pagesClause(source.pages);
              return (
                <li key={source.key}>
                  <button
                    onClick={(event) => {
                      const target = sourceTarget(source, citationQueryId);
                      if (target.calculation) {
                        setCard({
                          anchor: event.currentTarget,
                          calculation: target.calculation,
                        });
                        return;
                      }
                      // The row opens on the source's first citation; an agent
                      // answer's history read may have left its passage out
                      // (#12081).
                      const marker = source.markers[0];
                      void openChatCitationWithPassage(
                        { ...target, inlineViewerOnly },
                        passages && marker && needsPassage(target)
                          ? () => passages.passageFor(marker)
                          : undefined
                      );
                      onClose();
                    }}
                    // A fixed row height is what lets the list state "six rows
                    // visible" truthfully when it scrolls.
                    className="w-full h-[52px] flex items-start gap-2.5 text-left px-3 py-1.5 hover:bg-[var(--fp-band-hover)] transition-colors"
                  >
                    {/* The citation number, drawn as the chip in the prose is,
                      so the row reads as that chip's entry in the index. */}
                    <span
                      aria-label={`Citation ${source.number}`}
                      className="mt-0.5 inline-flex justify-center items-center shrink-0 w-4 h-4 text-[10px] bg-blue-200 text-blue-900 rounded-full"
                    >
                      {source.number}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] text-[var(--fp-ink)] truncate">
                        {chatSourceLabel(source.entry) || "Untitled source"}
                      </span>
                      <span className="block text-[11.5px] text-[var(--fp-ink-4)]">
                        {sourceKindLabel(source)}
                        {pages ? ` · ${pages}` : ""}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {scrolls && (
            // Rule 4: the truncation states its denominator, under the list.
            <div className="px-3 pt-1.5 pb-1 border-t border-[var(--fp-border)] text-[11.5px] text-[var(--fp-ink-4)]">
              {sources.length} sources
            </div>
          )}
        </div>
      </Popover>
      {card && (
        <CalculationCard
          anchorEl={anchorEl ? card.anchor : null}
          onClose={() => setCard(null)}
          calculation={card.calculation}
          citationQueryId={citationQueryId}
          inlineViewerOnly={inlineViewerOnly}
          // An input that opened its source is what the reader came for:
          // the list goes with the card, as a row that opens a source does.
          onInputOpened={onClose}
        />
      )}
    </>
  );
};

export default SourcesPopover;
